/**
 * Shared score-display utilities — KAN-127 Phase 0
 *
 * Single source of truth for "show points or percentage?" decisions and the
 * formatted score parts that every template reads. Keeps precision in ONE
 * place so it can never diverge across views.
 *
 * Exported surface:
 *   shouldShowPoints(data)              — boolean: are the points trustworthy?
 *   formatScoreParts(data, threshold)   — { attempted, showPoints, pointsText,
 *                                          percentText, passed }
 *
 * Dependencies (reused, never duplicated):
 *   formatScore      — from score-pts.pipe.ts (2-dp half-up, trailing-zero trim)
 *   isQuizPassing    — from @shared/utils/quiz-pass.utils (1-dp canonical pass/fail)
 *   formatPercentText— same module; the ONE quiz-percentage formatter (KAN-525)
 */

import { formatScore } from '../pipes/score-pts.pipe';
import { formatPercentText, isQuizPassing } from '@shared/utils/quiz-pass.utils';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface ScoreDisplayParts {
  /** false when the data has no score/earned value at all — caller renders "Not attempted". */
  attempted: boolean;
  /**
   * true  → points are real (total !== 100, or total===100 with consistent score)
   * false → points are migration-056 percentage-in-disguise; show pct only
   */
  showPoints: boolean;
  /** "{earned}/{total} pts" via formatScore — empty string when !attempted */
  pointsText: string;
  /** "{N}%" on the canonical 1-dp rule — empty string when !attempted */
  percentText: string;
  /** Authoritative pass/fail — drives the badge, never re-derived from rounded display */
  passed: boolean;
}

// ─── Field resolution helpers ────────────────────────────────────────────────

/**
 * Resolve the "earned points" value from any of the field aliases in use
 * across the five scattered implementations (KAN-127 D4).
 *
 * NOTE: The nested `quiz?.earnedPoints` / `quiz?.bestScore` fallbacks that
 * grade-book and learner-management-modal IIFEs use are resolved at data-mapping
 * time (before calling these utilities), not here. Keeping them out of the shared
 * fn keeps the alias union to the set that appears in actual progress-row shapes.
 */
function resolveEarned(data: any): number | null | undefined {
  return data?.quizEarnedPoints ?? data?.quiz_earned_points ?? data?.earnedPoints ?? data?.earned_points;
}

function resolveTotal(data: any): number | null | undefined {
  return data?.quizTotalPoints ?? data?.quiz_total_points ?? data?.totalPoints ?? data?.total_points;
}

function resolveScore(data: any): number | null | undefined {
  return data?.quizScore ?? data?.quiz_score;
}

// ─── shouldShowPoints ─────────────────────────────────────────────────────────

/**
 * Decide whether to display raw points alongside (or instead of) a percentage.
 *
 * This is the union of all five scattered implementations (KAN-127 D4), with
 * the total===0 guard standardised to ALWAYS be present (the one body that
 * was missing it — progress-viewer — was an acknowledged divergence).
 *
 * Rules (in order):
 *   1. Null/undefined data  → false
 *   2. Earned or total missing  → false
 *   3. total === 0  → false  (acknowledgement quiz: show "100%" not "0/0 pts")
 *   4. total !== 100  → true  (real points, not a percentage scale)
 *   5. total === 100, no score  → true  (no signal to compare against; default to points)
 *   6. total === 100, score present:
 *      |(earned/total*100) − score| < 1  → true  (consistent, real-ish points)
 *      else                              → false  (migration-056 backfill)
 */
export function shouldShowPoints(data: any): boolean {
  if (data === null || data === undefined) {
    return false;
  }

  const earned = resolveEarned(data);
  const total = resolveTotal(data);

  if (earned === null || earned === undefined || total === null || total === undefined) {
    return false;
  }

  const earnedNum = typeof earned === 'string' ? parseFloat(earned as string) : (earned as number);
  const totalNum = typeof total === 'string' ? parseFloat(total as string) : (total as number);

  // Acknowledgement quiz guard — always added (standardises on the defensive form)
  if (totalNum === 0) {
    return false;
  }

  if (totalNum !== 100) {
    return true;
  }

  // total === 100: check whether the stored score is consistent with the ratio
  const score = resolveScore(data);
  if (score !== null && score !== undefined && totalNum > 0) {
    const calculatedPercentage = (earnedNum / totalNum) * 100;
    const difference = Math.abs(calculatedPercentage - (score as number));
    return difference < 1;
  }

  // No score available to compare against — default to showing points
  return true;
}

// ─── formatScoreParts ─────────────────────────────────────────────────────────

/**
 * Produce all display-ready parts for a score badge in one call.
 *
 * Percentage rendering (KAN-525):
 *   pct = canonical percentage (data.percentage → quizScore → earned/total*100),
 *   rendered through formatPercentText — HALF_UP to one decimal place, the same
 *   value pass/fail is decided on (KAN-239). Because display and decision share
 *   one number, the badge can never visually contradict Passed/Failed, which is
 *   what the old integer-default-plus-clamp was working around.
 *
 * Override fields: when data.overriddenScore / data.overriddenEarnedPoints are
 * non-null, they shadow the original score / earnedPoints so the badge reflects
 * the instructor's adjustment.
 *
 * @param data       Any progress-row or submission shape (camelCase or snake_case)
 * @param threshold  Passing threshold percentage (e.g. 80 for 80%)
 */
export function formatScoreParts(data: any, threshold: number): ScoreDisplayParts {
  const notAttempted: ScoreDisplayParts = {
    attempted: false,
    showPoints: false,
    pointsText: '',
    percentText: '',
    passed: false,
  };

  if (data === null || data === undefined) {
    return notAttempted;
  }

  // ── Resolve override fields ──────────────────────────────────────────────
  // Overrides shadow original values when set by an instructor (KAN-D5).
  const hasOverride =
    data.overriddenScore !== null &&
    data.overriddenScore !== undefined &&
    data.overriddenEarnedPoints !== null &&
    data.overriddenEarnedPoints !== undefined;

  // ── Resolve earned / total ───────────────────────────────────────────────
  const rawEarned = resolveEarned(data);
  const rawTotal = resolveTotal(data);

  const earnedForDisplay: number | null | undefined = hasOverride
    ? (data.overriddenEarnedPoints as number)
    : (rawEarned === undefined ? undefined : rawEarned === null ? null : (typeof rawEarned === 'string' ? parseFloat(rawEarned) : rawEarned as number));

  const totalNum: number | null | undefined =
    rawTotal === undefined ? undefined : rawTotal === null ? null : (typeof rawTotal === 'string' ? parseFloat(rawTotal as string) : rawTotal as number);

  // ── Resolve raw score (integer percentage) ───────────────────────────────
  const rawScore = resolveScore(data);
  const scoreForPct: number | null | undefined = hasOverride
    ? (data.overriddenScore as number)
    : rawScore;

  // ── Determine "attempted" ────────────────────────────────────────────────
  // We need at least one of: a score field OR earned points — to know a submission exists.
  const hasScore = scoreForPct !== null && scoreForPct !== undefined;
  const hasEarned = earnedForDisplay !== null && earnedForDisplay !== undefined;

  if (!hasScore && !hasEarned) {
    return notAttempted;
  }

  // ── Acknowledgement quiz (total <= 0) ────────────────────────────────────
  // KAN-279: an acknowledgement/survey quiz still has a percentage — usually
  // 100% (completed), but an instructor can override it to 0% to annul the
  // attempt. Honour the score/override percentage and authoritative `passed`
  // rather than hardcoding "100% / passed". Default to 100%/passed only when no
  // score signal exists (the historical "just acknowledged" case).
  const isAck = totalNum !== null && totalNum !== undefined && totalNum <= 0;
  if (isAck) {
    const ackPct =
      data.percentage !== null && data.percentage !== undefined
        ? (data.percentage as number)
        : hasOverride
          ? (data.overriddenScore as number)
          : hasScore
            ? (scoreForPct as number)
            : 100;
    const ackPassed =
      typeof data.passed === 'boolean' ? data.passed : true;
    return {
      attempted: true,
      showPoints: false,
      pointsText: '',
      percentText: formatPercentText(ackPct),
      passed: ackPassed,
    };
  }

  // ── Compute canonical percentage (1-dp) ──────────────────────────────────
  // Preference order: data.percentage (new backend field, D2) → quizScore → earned/total*100
  let rawPct: number;
  if (data.percentage !== null && data.percentage !== undefined) {
    rawPct = data.percentage as number;
  } else if (hasOverride) {
    // Override: recalculate from override values
    if (totalNum !== null && totalNum !== undefined && totalNum > 0 && hasEarned) {
      rawPct = ((earnedForDisplay as number) / totalNum) * 100;
    } else {
      rawPct = data.overriddenScore as number;
    }
  } else if (hasScore) {
    rawPct = scoreForPct as number;
  } else if (hasEarned && totalNum !== null && totalNum !== undefined && totalNum > 0) {
    rawPct = ((earnedForDisplay as number) / totalNum) * 100;
  } else {
    return notAttempted;
  }

  // ── Authoritative pass/fail ──────────────────────────────────────────────
  // Prefer server-provided data.passed boolean when it's an actual boolean.
  // Fall back to isQuizPassing (uses 1-dp canonical rounding — KAN-239).
  const passed: boolean =
    typeof data.passed === 'boolean'
      ? data.passed
      : isQuizPassing(rawPct, threshold);

  // ── Display percentage ───────────────────────────────────────────────────
  // KAN-525: always the canonical 1-dp rendering. This used to emit an INTEGER
  // by default and drop to 1-dp only when the integer would contradict the
  // pass/fail badge - an anti-contradiction clamp that treated the symptom.
  // The cost of that default was that the same attempt read "93.8%" to the
  // learner (which uses the canonical rule) and "94%" in the Gradebook and the
  // audit view. Rendering the canonical value everywhere removes the
  // divergence AND the contradiction the clamp existed to paper over, since
  // pass/fail is decided on this very same 1-dp value (KAN-239).
  const percentText = formatPercentText(rawPct);

  // ── showPoints ────────────────────────────────────────────────────────────
  // Use the shared heuristic; for override rows, re-evaluate using the override values
  const dataForShowPoints = hasOverride
    ? { ...data, quizEarnedPoints: earnedForDisplay, quizScore: data.overriddenScore }
    : data;
  const showPoints = shouldShowPoints(dataForShowPoints);

  // ── pointsText ────────────────────────────────────────────────────────────
  const earnedStr = formatScore(hasEarned ? (earnedForDisplay as number | null | undefined) : undefined);
  const totalStr = formatScore(totalNum);
  const pointsText = `${earnedStr}/${totalStr} pts`;

  return {
    attempted: true,
    showPoints,
    pointsText,
    percentText,
    passed,
  };
}
